import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Pencil, Globe2, Clock } from "lucide-react";
import { toast } from "sonner";

const COMMON_TZ = [
  "UTC", "Asia/Kolkata", "Asia/Dubai", "Asia/Singapore", "Asia/Tokyo",
  "Europe/London", "Europe/Berlin", "Europe/Paris",
  "Africa/Lagos", "Africa/Nairobi", "Africa/Johannesburg",
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "America/Sao_Paulo",
  "Australia/Sydney",
];

export default function Countries() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", code: "", timezone: "UTC" });

  const load = () => api.get("/countries").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing(null); setForm({ name: "", code: "", timezone: "UTC" }); setOpen(true); };
  const startEdit = (c) => { setEditing(c); setForm({ name: c.name, code: c.code, timezone: c.timezone || "UTC" }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) {
        await api.patch(`/countries/${editing.id}`, form);
        toast.success("Country updated");
      } else {
        await api.post("/countries", form);
        toast.success("Country created");
      }
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this country?")) return;
    await api.delete(`/countries/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <>
      <PageHeader
        eyebrow="Organization"
        title="Countries"
        subtitle="Operational regions and their local timezones"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-country-btn" onClick={startCreate} className="rounded-none btn-primary-brand">
                <Plus className="h-4 w-4 mr-1" strokeWidth={2} /> New country
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none bg-card border-border">
              <DialogHeader><DialogTitle className="font-display">{editing ? "Edit country" : "New country"}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="label-mono">Name</Label>
                  <Input data-testid="country-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none" />
                </div>
                <div>
                  <Label className="label-mono">ISO Code</Label>
                  <Input data-testid="country-code-input" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="rounded-none font-mono-brand" maxLength={3} />
                </div>
                <div>
                  <Label className="label-mono">Timezone (IANA)</Label>
                  <Select value={form.timezone} onValueChange={(v) => setForm({ ...form, timezone: v })}>
                    <SelectTrigger data-testid="country-timezone-select" className="rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none bg-card max-h-72">
                      {COMMON_TZ.map(z => <SelectItem key={z} value={z}>{z}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="label-mono mt-1">Reminders will fire in this country's local time.</div>
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="country-save-btn" onClick={save} className="rounded-none btn-primary-brand">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="p-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((c) => (
          <Card key={c.id} data-testid={`country-card-${c.code}`} className="rounded-none border-border p-5 flex items-start justify-between">
            <div className="flex items-start gap-3">
              <Globe2 className="h-5 w-5 text-muted-foreground mt-0.5" strokeWidth={1.5} />
              <div>
                <div className="label-mono">{c.code}</div>
                <div className="font-display text-lg leading-tight">{c.name}</div>
                <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground font-mono-brand">
                  <Clock className="h-3 w-3" strokeWidth={1.5} /> {c.timezone || "UTC"}
                </div>
              </div>
            </div>
            <div className="flex gap-1">
              <button onClick={() => startEdit(c)} className="p-2 border border-border hover:bg-secondary"><Pencil className="h-3.5 w-3.5" strokeWidth={1.5} /></button>
              <button onClick={() => del(c.id)} className="p-2 border border-border hover:bg-secondary"><Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} /></button>
            </div>
          </Card>
        ))}
      </div>
    </>
  );
}
